import { useCallback, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from '@dagrejs/dagre';

// Types matching WorldState structure
interface WorldStateData {
  entities?: {
    characters?: Record<
      string,
      { name?: string; status?: string; traits?: string[]; aliases?: string[] }
    >;
    places?: Record<string, { name?: string; description?: string; aliases?: string[] }>;
    factions?: Record<string, { name?: string; description?: string; aliases?: string[] }>;
    items?: Record<string, { name?: string; description?: string; aliases?: string[] }>;
  };
  relationships?: Array<{
    a: string;
    b: string;
    type: string;
    strength: number;
  }>;
  timeline?: {
    t_current?: string;
    events?: Record<
      string,
      {
        title: string;
        participants: string[];
        day_index?: number;
      }
    >;
  };
  threads?: {
    open?: Record<string, { title: string; stakes?: string }>;
    closed?: Record<string, { title: string; resolution?: string }>;
  };
}

interface WorldGraphProps {
  data: WorldStateData;
}

function normalizeKey(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

function titleFromId(id: string) {
  const cleaned = id.replace(/^(character|place|faction|item)-/i, '');
  const parts = cleaned.split(/[-_]/).filter(Boolean);
  if (parts.length === 0) return id;
  return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

function getDisplayName(id: string, name?: string) {
  if (!name || name.trim().length === 0 || name.toLowerCase() === 'unknown') {
    return titleFromId(id);
  }
  return name;
}

// Entity type colors
const entityColors = {
  character: { bg: '#0d9488', border: '#14b8a6', text: '#f0fdfa' }, // teal
  place: { bg: '#d97706', border: '#f59e0b', text: '#fffbeb' }, // amber
  faction: { bg: '#7c3aed', border: '#8b5cf6', text: '#f5f3ff' }, // purple
  item: { bg: '#6b7280', border: '#9ca3af', text: '#f9fafb' }, // gray
  event: { bg: '#dc2626', border: '#ef4444', text: '#fef2f2' }, // red
  thread: { bg: '#0284c7', border: '#0ea5e9', text: '#f0f9ff' }, // sky
};

// Layout using dagre
function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
  direction = 'TB',
): { nodes: Node[]; edges: Edge[] } {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, nodesep: 80, ranksep: 100 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 180, height: 50 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 90,
        y: nodeWithPosition.y - 25,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

export function WorldGraph({ data }: WorldGraphProps) {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const entityNodeIds = new Set<string>();
    const nameToEntityId = new Map<string, string>();
    const participantNodeIds = new Map<string, string>();

    const entities = data.entities ?? {};
    const characters = entities.characters ?? {};
    const places = entities.places ?? {};
    const factions = entities.factions ?? {};
    const items = entities.items ?? {};
    const relationships = data.relationships ?? [];
    const timelineEvents = data.timeline?.events ?? {};
    const threads = data.threads ?? {};
    const openThreads = threads.open ?? {};

    const registerEntityName = (id: string, name?: string, aliases?: string[]) => {
      const displayName = getDisplayName(id, name);
      const normalizedName = normalizeKey(displayName);
      if (normalizedName) {
        nameToEntityId.set(normalizedName, id);
      }
      if (name) {
        const normalizedRaw = normalizeKey(name);
        if (normalizedRaw) {
          nameToEntityId.set(normalizedRaw, id);
        }
      }
      if (aliases) {
        aliases.forEach((alias) => {
          const normalizedAlias = normalizeKey(alias);
          if (normalizedAlias) {
            nameToEntityId.set(normalizedAlias, id);
          }
        });
      }
      const slugFromId = normalizeKey(id.replace(/^(character|place|faction|item)-/i, ''));
      if (slugFromId) {
        nameToEntityId.set(slugFromId, id);
      }
      return displayName;
    };

    const getParticipantNodeId = (participant: string) => {
      const normalized = normalizeKey(participant);
      const match = nameToEntityId.get(normalized);
      if (match) {
        return match;
      }
      if (participantNodeIds.has(participant)) {
        return participantNodeIds.get(participant) as string;
      }
      const fallbackId = `participant-${normalized || participantNodeIds.size}`;
      participantNodeIds.set(participant, fallbackId);
      nodes.push({
        id: fallbackId,
        data: {
          label: participant,
          subtitle: 'Participant',
        },
        position: { x: 0, y: 0 },
        style: {
          background: '#0d948833',
          border: `2px dashed ${entityColors.character.border}`,
          color: entityColors.character.text,
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '11px',
          fontWeight: 500,
        },
      });
      return fallbackId;
    };

    // Create nodes for each entity type
    Object.entries(characters).forEach(([id, char]) => {
      const displayName = registerEntityName(id, char.name, char.aliases);
      entityNodeIds.add(id);
      nodes.push({
        id,
        data: {
          label: displayName,
          subtitle: char.status ?? 'Character',
        },
        position: { x: 0, y: 0 },
        style: {
          background: entityColors.character.bg,
          border: `2px solid ${entityColors.character.border}`,
          color: entityColors.character.text,
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '12px',
          fontWeight: 500,
        },
      });
    });

    Object.entries(places).forEach(([id, place]) => {
      const displayName = registerEntityName(id, place.name, place.aliases);
      entityNodeIds.add(id);
      nodes.push({
        id,
        data: {
          label: displayName,
          subtitle: 'Place',
        },
        position: { x: 0, y: 0 },
        style: {
          background: entityColors.place.bg,
          border: `2px solid ${entityColors.place.border}`,
          color: entityColors.place.text,
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '12px',
          fontWeight: 500,
        },
      });
    });

    Object.entries(factions).forEach(([id, faction]) => {
      const displayName = registerEntityName(id, faction.name, faction.aliases);
      entityNodeIds.add(id);
      nodes.push({
        id,
        data: {
          label: displayName,
          subtitle: 'Faction',
        },
        position: { x: 0, y: 0 },
        style: {
          background: entityColors.faction.bg,
          border: `2px solid ${entityColors.faction.border}`,
          color: entityColors.faction.text,
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '12px',
          fontWeight: 500,
        },
      });
    });

    Object.entries(items).forEach(([id, item]) => {
      const displayName = registerEntityName(id, item.name, item.aliases);
      entityNodeIds.add(id);
      nodes.push({
        id,
        data: {
          label: displayName,
          subtitle: 'Item',
        },
        position: { x: 0, y: 0 },
        style: {
          background: entityColors.item.bg,
          border: `2px solid ${entityColors.item.border}`,
          color: entityColors.item.text,
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '12px',
          fontWeight: 500,
        },
      });
    });

    // Create edges from relationships
    relationships.forEach((rel, index) => {
      // Only create edges if both nodes exist
      if (entityNodeIds.has(rel.a) && entityNodeIds.has(rel.b)) {
        const normalizedStrength = Math.abs(rel.strength);
        const isNegative = rel.strength < 0;
        edges.push({
          id: `rel-${index}`,
          source: rel.a,
          target: rel.b,
          label: rel.type,
          animated: normalizedStrength > 0.7,
          style: {
            stroke: isNegative ? '#dc2626' : normalizedStrength > 0.5 ? '#8b5cf6' : '#6b7280',
            strokeWidth: Math.max(1, normalizedStrength * 3),
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 15,
            height: 15,
            color: isNegative ? '#dc2626' : normalizedStrength > 0.5 ? '#8b5cf6' : '#6b7280',
          },
        });
      }
    });

    // Add timeline events as nodes (limit to recent events)
    const events = Object.entries(timelineEvents).slice(-10);
    events.forEach(([id, event]) => {
      const eventNodeId = `event-${id}`;
      nodes.push({
        id: eventNodeId,
        data: {
          label: event.title ?? `Event ${id}`,
          subtitle: event.day_index !== undefined ? `Day ${event.day_index}` : 'Event',
        },
        position: { x: 0, y: 0 },
        style: {
          background: entityColors.event.bg,
          border: `2px solid ${entityColors.event.border}`,
          color: entityColors.event.text,
          borderRadius: '16px',
          padding: '8px 16px',
          fontSize: '11px',
          fontWeight: 500,
        },
      });

      // Link event to participants
      (event.participants ?? []).forEach((participantId, idx) => {
        const targetId = entityNodeIds.has(participantId)
          ? participantId
          : getParticipantNodeId(participantId);
        edges.push({
          id: `event-${id}-${participantId}-${idx}`,
          source: eventNodeId,
          target: targetId,
          style: {
            stroke: entityColors.event.border,
            strokeWidth: 1,
            strokeDasharray: '5,5',
          },
        });
      });
    });

    // Add open threads as nodes
    Object.entries(openThreads).forEach(([id, thread]) => {
      nodes.push({
        id: `thread-${id}`,
        data: {
          label: thread.title,
          subtitle: 'Open Thread',
        },
        position: { x: 0, y: 0 },
        style: {
          background: entityColors.thread.bg,
          border: `2px solid ${entityColors.thread.border}`,
          color: entityColors.thread.text,
          borderRadius: '4px',
          padding: '8px 16px',
          fontSize: '11px',
          fontWeight: 500,
          fontStyle: 'italic',
        },
      });
    });

    // Apply layout
    return getLayoutedElements(nodes, edges, 'TB');
  }, [data]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onLayout = useCallback(
    (direction: 'TB' | 'LR') => {
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        nodes,
        edges,
        direction,
      );
      setNodes([...layoutedNodes]);
      setEdges([...layoutedEdges]);
    },
    [nodes, edges, setNodes, setEdges],
  );

  if (nodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-mist/60">
        No entities in this world state yet.
      </div>
    );
  }

  return (
    <div className="h-full bg-void overflow-hidden relative">
      {/* Legend */}
      <div className="absolute top-4 left-4 z-10 bg-night/90 border border-stone/30 rounded-lg p-3 text-xs space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded" style={{ background: entityColors.character.bg }} />
          <span className="text-mist">Characters</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded" style={{ background: entityColors.place.bg }} />
          <span className="text-mist">Places</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded" style={{ background: entityColors.faction.bg }} />
          <span className="text-mist">Factions</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded" style={{ background: entityColors.item.bg }} />
          <span className="text-mist">Items</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ background: entityColors.event.bg }} />
          <span className="text-mist">Events</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3" style={{ background: entityColors.thread.bg }} />
          <span className="text-mist">Threads</span>
        </div>
      </div>

      {/* Layout buttons */}
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <button
          onClick={() => onLayout('TB')}
          className="px-3 py-1 bg-night/90 border border-stone/30 rounded text-xs text-mist hover:text-parchment"
        >
          Vertical
        </button>
        <button
          onClick={() => onLayout('LR')}
          className="px-3 py-1 bg-night/90 border border-stone/30 rounded text-xs text-mist hover:text-parchment"
        >
          Horizontal
        </button>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        minZoom={0.1}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      >
        <Background color="#2a2a3a" gap={20} size={1} />
        <Controls
          showZoom
          showFitView
          showInteractive={false}
          style={{
            background: '#12121a',
            border: '1px solid #2a2a3a',
            borderRadius: '8px',
          }}
        />
        <MiniMap
          nodeColor={(node) => {
            const style = node.style as { background?: string } | undefined;
            return style?.background ?? '#6b7280';
          }}
          maskColor="#0a0a0f99"
          style={{
            background: '#12121a',
            border: '1px solid #2a2a3a',
            borderRadius: '8px',
          }}
        />
      </ReactFlow>
    </div>
  );
}
